import React, { useState } from 'react';
import styles from './PcosRiskAssessment.module.css';

/**
 * ConsentScreen — reusable for PCOS, Pregnancy, or any screening flow.
 *
 * Props:
 *   title        {string}   — screen heading (default: "Before we begin")
 *   description  {string[]} — array of paragraphs shown under the heading
 *   checkLabel   {string}   — checkbox label text
 *   onAgree      {fn}       — called when user clicks the CTA
 *   onBack       {fn}       — called when user clicks the back arrow
 *   step         {number}   — current step number (shown in progress bar)
 *   totalSteps   {number}   — total steps (shown in progress bar)
 *   ctaLabel     {string}   — CTA button text (default: "I agree, continue")
 */
export const ConsentScreenPCOS = ({
  title = 'Before we begin',
  description = [
    'This screening is for informational purposes only and not a medical diagnosis.',
    'Please confirm that you understand and consent to proceed.',
  ],
  checkLabel = 'I agree that this screening does not replace professional medical advice, diagnosis or treatment.',
  onAgree,
  onBack,
  step = 1,
  totalSteps = 10,
  ctaLabel = 'I agree, continue',
}) => {
  const [checked, setChecked] = useState(false);

  const progress = totalSteps > 0 ? (step / totalSteps) * 100 : 0;

  return (
      <div className={styles.card}>

        {/* ── top bar ── */}
        <div className={styles.topBar}>
          <div>
  <img src='/Home/logo.svg' alt='logo' style={{height:"32px"}}/>
</div>
          <button className={styles.backBtn} onClick={onBack} aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          {/* progress bar */}
          {/* <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
          </div> */}
        </div>

        {/* <p className={styles.stepLabel}>Step {step} of {totalSteps}</p> */}

        {/* ── illustration ── */}
        <div className={styles.illustrationWrap}>
          <div className={styles.illustrationBg} />
          {/* Shield + clipboard SVG illustration */}
          <svg className={styles.illustration} viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* decorative dots */}
            <circle cx="30" cy="30" r="4" fill="var(--primary-light)" opacity="0.5" />
            <circle cx="170" cy="40" r="5" fill="var(--secondary-light)" opacity="0.5" />
            <circle cx="155" cy="150" r="3" fill="var(--primary-light)" opacity="0.4" />
            <circle cx="20" cy="140" r="6" fill="var(--secondary-light)" opacity="0.3" />

            {/* leaves left */}
            <ellipse cx="35" cy="110" rx="14" ry="28" fill="var(--primary-light)" opacity="0.35" transform="rotate(-30 35 110)" />
            <ellipse cx="25" cy="130" rx="10" ry="20" fill="var(--primary)" opacity="0.18" transform="rotate(-15 25 130)" />

            {/* leaves right */}
            <ellipse cx="168" cy="115" rx="14" ry="26" fill="var(--secondary-light)" opacity="0.35" transform="rotate(25 168 115)" />
            <ellipse cx="178" cy="135" rx="9" ry="18" fill="var(--secondary)" opacity="0.18" transform="rotate(10 178 135)" />

            {/* shield */}
            <path d="M60 50 L60 110 Q60 138 100 150 Q140 138 140 110 L140 50 L100 38 Z"
              fill="var(--primary)" opacity="0.15" />
            <path d="M64 54 L64 110 Q64 134 100 145 Q136 134 136 110 L136 54 L100 43 Z"
              fill="var(--primary)" opacity="0.25" />
            <path d="M68 58 L68 110 Q68 130 100 140 Q132 130 132 110 L132 58 L100 48 Z"
              fill="var(--primary)" opacity="0.6" />
            {/* shield check */}
            <circle cx="100" cy="97" r="18" fill="white" opacity="0.35" />
            <circle cx="100" cy="97" r="14" fill="white" opacity="0.5" />
            <polyline points="90,97 97,104 112,89"
              stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

            {/* clipboard */}
            <rect x="105" y="58" width="52" height="64" rx="6" fill="white"
              stroke="var(--secondary-light)" strokeWidth="1.5" />
            <rect x="118" y="54" width="26" height="10" rx="5"
              fill="var(--secondary-light)" />
            {/* clipboard lines */}
            {[76, 88, 100].map((y, i) => (
              <g key={i}>
                <circle cx="116" cy={y} r="4" fill="var(--primary)" opacity="0.7" />
                <polyline points={`113,${y} 115,${y + 2} 120,${y - 3}`}
                  stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                <rect x="124" y={y - 2} width="24" height="3" rx="1.5"
                  fill="var(--secondary-light)" opacity="0.6" />
              </g>
            ))}

            {/* lock */}
            <rect x="128" y="106" width="22" height="18" rx="4"
              fill="var(--secondary)" opacity="0.85" />
            <path d="M133 106 Q133 98 139 98 Q145 98 145 106"
              stroke="var(--secondary)" strokeWidth="2.5" fill="none"
              strokeLinecap="round" opacity="0.85" />
            <circle cx="139" cy="115" r="3" fill="white" opacity="0.8" />
          </svg>
        </div>

        {/* ── content ── */}
        <div className={styles.content}>
          <h2 className={styles.title}>{title}</h2>

          {description.map((para, i) => (
            <p key={i} className={styles.description}>{para}</p>
          ))}

          {/* ── checkbox ── */}
          <label className={styles.checkboxLabel}>
            <span className={`${styles.checkBox} ${checked ? styles.checkBoxChecked : ''}`}
              onClick={() => setChecked((v) => !v)}
              role="checkbox"
              aria-checked={checked}
              tabIndex={0}
              onKeyDown={(e) => e.key === ' ' && setChecked((v) => !v)}
            >
              {checked && (
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <polyline points="2,6.5 5.5,10 11,3"
                    stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className={styles.checkboxText}>{checkLabel}</span>
          </label>
        </div>

        {/* ── CTA ── */}
        <div className={styles.footer}>
          <button
            className={` btn-primary ${styles.ctaBtn}`}
            disabled={!checked}
            onClick={() => checked && onAgree?.()}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
  );
};

export default ConsentScreenPCOS;